import React, { useState, useEffect } from 'react';
import { Container, Col, Row, Button, Modal, Badge, Form } from 'react-bootstrap';
import arrowLeft from "../assets/images/arrow-left.png";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import config from '../config/config';
import { showToast } from '../config/Alert';
import { useCart } from "../context/CartContext";
import { applyCoupon } from '../services/CouponApi';
import { processBooking, getCartItems, renewBookingSubscription } from '../services/CartApi';
import LoadingSpinner from './LoadingSpinner';
import deleteIcon from "../assets/images/delete.png";

const Checkout = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  // const [toastMessage, setToastMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState("");
  const { removeFromCart, fetchCart } = useCart();
  const location = useLocation();
  const subscriptionId = location.state?.subscriptionId || null;

  useEffect(() => {
    fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    try {
      const response = await getCartItems(user.id);
      if (Array.isArray(response.data)) {
        setCartItems(response.data);
        // console.log("Cart Items for Total:", response.data.map(item => ({
        //   name: item.child?.child_name,
        //   amount: item.payment_plan?.final_amount
        // })));
        const total = response.data.reduce((sum, item) => {
          const finalAmount = parseFloat(item.payment_plan?.final_amount) || 0;
          return sum + finalAmount;
        }, 0);

        setTotalAmount(total);

      } else {
        console.error("Expected an array but got:", response.data);
        setCartItems([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching cart items:", error);
      setLoading(false);
    }
  };

  const handleDeleteCartItem = async () => {
    setLoading(true);
    const response = await removeFromCart(itemToDelete);

    if (response.ok) {
     // setCartItems(cartItems.filter(item => item.id !== itemToDelete));
     // console.log("cart items ", cartItems);
      await fetchCartItems();
      setShowModal(false);
      showToast("Item deleted successfully!", 'success');
      setLoading(false);
    } else {
      showToast("Failed to delete cart item", 'error');
      setLoading(false);
    }
  };

  const confirmDelete = (itemId) => {
    setItemToDelete(itemId);
    setShowModal(true);
  };

  const handleApplyCoupon = async () => {
    try {
      const response = await applyCoupon({ coupon_code: couponCode });
      const data = await response.data.coupon;
      // Check if it's for the cart (programs)
      if (data.coupon_for === "Program") {
        setCouponData(data);
        setCouponError("");
      } else {
        setCouponData(null);
        setCouponError("This coupon is not applicable to program subscriptions");
      }
    } catch (error) {
      setCouponData(null);
      setCouponError(error.message);
    }
  };

  const calculateDiscountAmount = () => {
    if (!couponData) return 0;

    const { coupon_type, value } = couponData;

    if (coupon_type === "amount") {
      return parseFloat(value);
    } else if (coupon_type === "percent") {
      return totalAmount * parseFloat(value) / 100;
    }
    return 0;
  };

  const getDiscountedTotal = () => {
    return Math.max(totalAmount - calculateDiscountAmount(), 0);
  };

  const handlePaymentSuccess = async (paymentId) => {
    setLoading(true);
    try {
      const params = {
        user_id: user.id,
        total_amount: totalAmount,
        transaction_id: paymentId
      };
      const isRenewal = cartItems.some(item => item.status_key === 'renewal');
      if (isRenewal && subscriptionId) {
        await renewBookingSubscription(params, subscriptionId);
      } else {
        await processBooking(params);
      }
      await fetchCart();
      showToast("Payment was Successful!", 'success');
      setLoading(false);
      navigate(`/programs`, { state: { activeTab: 'my' } });
    } catch (error) {
      console.error("Error processing payment:", error);
      showToast("Payment Failed", 'error');
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (cartItems.length === 0) {
      showToast("Your cart is empty. Add items before proceeding.", 'info');
      return;
    }

    const options = {
      key: config.Razorpay_Key, // Replace with your Razorpay key ID
      amount: getDiscountedTotal().toFixed(2) * 100, // Amount in paise
      currency: 'INR',
      name: 'The Pied Piper',
      description: 'Course Payment',
      handler: function (response) {
        setPaymentId(response.razorpay_payment_id); // Set the payment ID
        handlePaymentSuccess(response.razorpay_payment_id); // Send booking data
      },
      prefill: {
        name: user?.name,
        email: user?.email,
        contact: ''
      },
      theme: {
        color: '#F37254'
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      console.error(response.error);
      setShowFailureModal(true); // Show the failure modal
    });
    rzp.open();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='app-container mobile-bottom'>
      <Container>
        <div className='breadcrum bg_light_yellow'>
          <h2 className='d-flex align-items-center mb-0 fs-20'>
            <Link to={`/programs`}><img src={arrowLeft} width={18} className='me-2' alt="link" /></Link>
            Cart</h2>
        </div>

        {cartItems.length > 0 ?
          <>
            <Row className='pt-4 mb-4'>
              {cartItems.map((item, index) => (
                <Col xs='12' key={index} className='mb-3'>
                  <div className='d-flex justify-content-between align-items-center border p-2 rounded-3'>
                    <div className='d-flex gap-3'>
                      <div className='cart-image'>
                        <img src={`${config.Image_Base_URL}${item.child.profile_pic_url}`} alt={item.child.child_name} width="50" height="50" />
                      </div>
                      <div>
                        <h4 className='fw-7 fs-16'>{item.child.child_name}</h4>
                        {item.time_slots.map((slot, idx) => (
                          <h6 key={idx} className='fs-12 mb-1'>
                            <span className='text-primary fw-7'>Slot-{idx + 1} :</span>
                            <span>{slot.start_time} - {slot.end_time}, {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][slot.day]}</span>
                          </h6>
                        ))}
                        <p className='fw-4 fs-12 mb-0'>
                          <span className='text-danger fw-7'>Course Details: </span>
                          <span className='fw-6'>{item?.program_focus?.title}</span> {item?.program_focus?.title && `in`}
                          <span className='fw-6'> {item.program.program_name}</span> -
                          <span className='fw-6'> {item.skill_level.skill_name}</span>,
                          <span className='fw-6'> {item.payment_plan.duration_months} Month(s)</span>
                        </p>
                      </div>
                    </div>
                    <div className='text-end'>
                      <h4 className='fw-7 fs-14'>₹ {item.payment_plan?.final_amount}</h4>
                      <Button variant='unknown' className='p-0' size='sm' onClick={() => confirmDelete(item.id)}>
                        <img src={deleteIcon} alt="delete" width={18} />
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}

              {/* Apply Coupon Code */}
              <div className='border-top pt-3 mb-3'>
                <p className='mb-0 fs-12 fw-5'>Promo Code / Coupon</p>
                <div className='d-flex gap-2'>
                  <Form.Group controlId="exampleForm.ControlInput1">
                    <Form.Control
                      size='sm' type="text" placeholder="Enter here" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} />
                  </Form.Group>
                  <Form.Group controlId="exampleForm.ControlInput1">
                    <Button variant='danger' size='small' type='button' className='fw-7 fs-12' onClick={handleApplyCoupon}>Apply</Button>
                  </Form.Group>
                </div>
                {couponData && <small className='text-success fw-5 fs-10 d-block'>Coupon code applied successfully</small>}
                {couponError && <small className='text-danger fw-5 fs-10 d-block'>{couponError}</small>}
              </div>

              {/* <div className='total-amount d-flex justify-content-between'>
                <div>
                  <h4 className='fw-7 fs-16'>Total Amount</h4>
                </div>
                <div>
                  <h4 className='fw-7 fs-14 pe-1'>₹ {totalAmount.toFixed(2)}</h4>
                </div>
              </div> */}

              {/* Amount Summary */}
              <div className='border-top pt-3 mb-3'>
                <div className='d-flex justify-content-between'>
                  <h6 className='fw-6 fs-12 mb-1'>Total Amount</h6>
                  <h6 className='fw-6 fs-12 mb-1'>₹ {totalAmount.toFixed(2)}</h6>
                </div>

                {couponData && (
                  <div className='d-flex justify-content-between'>
                    <h6 className='fw-6 fs-12 text-success mb-1'>
                      Discount ({couponData.coupon_type === "percent" ? `${couponData.value}%` : `₹ ${couponData.value}`})
                    </h6>
                    <h6 className='fw-6 fs-12 text-success mb-1'>
                      - ₹ {calculateDiscountAmount().toFixed(2)}
                    </h6>
                  </div>
                )}

                <div className='d-flex justify-content-between border-top pt-2 mt-2'>
                  <h5 className='fw-7 fs-14 text-dark mb-0'>To Pay</h5>
                  <h5 className='fw-7 fs-14 text-dark mb-0'>₹ {getDiscountedTotal().toFixed(2)}</h5>
                </div>
              </div>
              <div className='d-flex gap-2'>
                <Button variant='warning' type='button' className='fw-7 fs-12 me-2 rounded-4' onClick={handlePayment}>Proceed to checkout</Button>
                <Button variant='info' type='button' className='fw-7 fs-12 rounded-4' onClick={() => navigate('/programs', { state: { activeTab: 'all' } })}>Explore other courses</Button>
              </div>
            </Row>

          </>
          : <div className='text-center pt-5 mt-5'>
            <h5>Your Cart is <span className='text-danger'> Empty!</span></h5>
            <p className='py-2'>Must add items on the cart before you procced to check our.</p>
            <Button variant="danger" className='rounded-5 text-white' onClick={() => navigate(`/programs`)}>
              Choose the Program
            </Button>
          </div>}
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className='fs-16'>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center'>Are you sure you want to delete this item?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className='rounded-4 px-3' size='sm' onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" className='rounded-4 px-3' size='sm' onClick={handleDeleteCartItem}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}> {/* Success Modal */}
        <Modal.Header closeButton>
          <Modal.Title className='text-success fs-20'>Payment Successful</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center'>
          <div>
            <p>Payment ID </p>
            <Badge bg="success fs-14" className=''>{paymentId}</Badge></div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showFailureModal} onHide={() => setShowFailureModal(false)}> {/* Failure Modal */}
        <Modal.Header closeButton>
          <Modal.Title className='text-danger fs-20'>Payment Failed</Modal.Title>
        </Modal.Header>
        <Modal.Body>Payment failed. Please try again.</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size='sm' onClick={() => setShowFailureModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Checkout;